import React, { useCallback, useEffect, useMemo, useState, FormEvent, useRef } from "react";

import { throttle } from "lodash";

import { ProductData, ProductStatus, ExampleMedia } from "data/product/products";
import { StorefrontData } from "data/storefront";

import {
  updateProductDraft,
  createProductDraft,
  ProductDraft,
  StorefrontDraft,
  updateStorefrontDraft,
} from "~/api/creatorApi";
import { ApiStatus } from "~/api/fetchStudioApi";

import { CreatorSavingType } from "../useCreatorEditing";

interface CreatorEditingProps {
  children: React.ReactNode;
  productData: ProductData;
  storefrontData: StorefrontData;
  isInEditMode: boolean;
}

interface CreatorFieldsTexts {
  creatorName: string;
  shortBio: string;
  title: string;
  description: string;
  storefrontDescription: string;
  overviewSectionTitle: string;
  overviewSectionDescription: string;
  exampleMediaSectionTitle: string | undefined;
  exampleMediaSectionDescription: string | undefined;
}

interface CreatorEditingState {
  fieldsTexts: CreatorFieldsTexts;
  fieldsOnError: string[];
  isSavingChanges: boolean;
  savingStatusText: string;
  isAISuggestionsShown: boolean;
  handleTextChange: (
    e: FormEvent<HTMLHeadingElement>,
    key: string,
    savingType: CreatorSavingType,
  ) => void;
  handleTextFieldUpdate: (text: string, key: string, savingType: CreatorSavingType) => void;
  handleTextEdit: (
    e: FormEvent<HTMLHeadingElement>,
    key: string,
    savingType: CreatorSavingType,
  ) => void;
  setSavingStatusText: (statusText: string) => void;
  setIsSavingChanges: (isSavingChanges: boolean) => void;
  handleSetIsAISuggestionsShown: (isAISuggestionsShown: boolean) => void;
  saveProductDraftUpdate: (
    existingProduct: ProductData,
    shouldThrottleSaving: boolean,
    key?: string,
    value?: string | string[] | ExampleMedia[],
  ) => any;
  saveStorefrontDraftUpdate: (
    existingStorefront: StorefrontDraft,
    shouldThrottleSaving: boolean,
    key?: string,
    value?: string | string[],
  ) => any;
  CHANGES_SAVING_TEXT: string;
  CHANGES_SAVED_TEXT: string;
  CHANGES_ERROR_TEXT: string;
  CHANGES_LIMIT_TEXT: string;
}

export const CreatorEditingContext = React.createContext<CreatorEditingState>({
  fieldsTexts: {
    creatorName: "",
    shortBio: "",
    title: "",
    description: "",
    storefrontDescription: "",
    overviewSectionTitle: "",
    overviewSectionDescription: "",
    exampleMediaSectionTitle: "",
    exampleMediaSectionDescription: "",
  },
  fieldsOnError: [],
  isSavingChanges: false,
  isAISuggestionsShown: true,
  savingStatusText: "",
  CHANGES_SAVING_TEXT: "",
  CHANGES_SAVED_TEXT: "",
  CHANGES_ERROR_TEXT: "",
  CHANGES_LIMIT_TEXT: "",
  handleTextChange: async () => ({ type: "error", error: new Error("not initialized") }),
  handleTextFieldUpdate: async () => ({ type: "error", error: new Error("not initialized") }),
  handleTextEdit: async () => ({ type: "error", error: new Error("not initialized") }),
  setSavingStatusText: async () => ({ type: "error", error: new Error("not initialized") }),
  setIsSavingChanges: async () => ({ type: "error", error: new Error("not initialized") }),
  handleSetIsAISuggestionsShown: async () => ({
    type: "error",
    error: new Error("not initialized"),
  }),
  saveProductDraftUpdate: async () => ({ type: "error", error: new Error("not initialized") }),
  saveStorefrontDraftUpdate: async () => ({ type: "error", error: new Error("not initialized") }),
});

export const CreatorEditingProvider = ({
  children,
  productData,
  storefrontData,
  isInEditMode,
}: CreatorEditingProps) => {
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isAISuggestionsShown, setIsAISuggestionsShown] = useState(false);
  const [savingStatusText, setSavingStatusText] = useState("");
  const [fieldsOnError, setFieldsOnError] = useState<string[]>([]);
  const [fieldsTexts, setFieldsTexts] = useState({
    creatorName: storefrontData?.creatorName,
    shortBio: storefrontData?.shortBio,
    title: productData.title,
    description: productData.description,
    storefrontDescription: storefrontData?.description,
    overviewSectionTitle: productData.overviewSectionTitle,
    overviewSectionDescription: productData.overviewSectionDescription,
    exampleMediaSectionTitle: productData.exampleMediaSectionTitle,
    exampleMediaSectionDescription: productData.exampleMediaSectionDescription,
  });

  interface CharacterMaxLengths {
    [key: string]: number;
  }
  const characterMaxLengthsPerField: CharacterMaxLengths = {
    creatorName: 50,
    shortBio: 185,
    title: 89,
    description: 4000,
  };

  // const handleSetIsAISuggestionsShown = (isAISuggestionsShown: boolean) => {

  //   setIsAISuggestionsShown(isAISuggestionsShown);
  // };

  const handleSetIsAISuggestionsShown = useCallback(
    (newValue: boolean) => {

      setIsAISuggestionsShown(newValue);
    },
    [isAISuggestionsShown],
  );

  const saveProductDraftUpdate = async (
    existingProduct: ProductData,
    shouldThrottleSaving: boolean,
    key?: string,
    value?: string | string[] | ExampleMedia[],
  ) => {

    // if (!isInEditMode) {
    //   return;
    // }
    let draftUpdate: ProductDraft = {
      id: existingProduct.id,
      status: ProductStatus.DRAFT,
      createdAt: existingProduct.createdAt,
      lastUpdatedAt: existingProduct.lastUpdatedAt,
    };

    // If we send a specific key and value, only update that.
    if (key && value) {
      draftUpdate = { ...draftUpdate, [key]: value };
      // Otherwise, send the whole Product data info to update.
    } else {
      draftUpdate = { ...existingProduct, ...draftUpdate };
    }



    if (shouldThrottleSaving) {
      const result = await throttledUpdateProductDraft(draftUpdate);

      return result;
    } else {
      const flush = await throttledUpdateProductDraft.flush(); //Make sure there's no concurrency issues.

      const result = await updateProductDraft(draftUpdate);

      return result;
    }
  };

  // Use memo to prevent the function from being created again on rerender
  const throttledUpdateProductDraft = useMemo(
    () =>
      throttle(async (draftUpdate: ProductDraft) => {
        const result = await updateProductDraft(draftUpdate);
        return result;
      }, 3000),
    [],
  );

  const createNewProductDraft = async () => {
    const result = await createProductDraft();

  };

  const CHANGES_SAVED_TEXT = "All changes saved";
  const CHANGES_SAVING_TEXT = "Saving changes...";
  const CHANGES_ERROR_TEXT = "Error saving changes... Please try again or contact support";
  const CHANGES_LIMIT_TEXT = "Over character limit... Try shorter text to save";

  const saveStorefrontDraftUpdate = async (
    existingStorefront: StorefrontDraft,
    shouldThrottleSaving: boolean,
    key?: string,
    value?: string | string[],
  ) => {
    let draftUpdate: StorefrontDraft = {
      id: existingStorefront.id,
    };
    // If we send a specific key and value, only update that.
    if (key && value) {
      draftUpdate = { ...draftUpdate, [key]: value };
      // Otherwise, send the whole Product data info to update.
    } else {
      draftUpdate = { ...existingStorefront, ...draftUpdate };
    }



    if (shouldThrottleSaving) {
      const result = await throttledUpdateStorefrontDraft(draftUpdate);

      return result;
    } else {
      const flush = await throttledUpdateStorefrontDraft.flush(); //Make sure there's no concurrency issues.

      const result = await updateStorefrontDraft(draftUpdate);

      return result;
    }
  };

  // Use memo to prevent the function from being created again on rerender
  const throttledUpdateStorefrontDraft = useMemo(
    () =>
      throttle(async (draftUpdate: StorefrontDraft) => {
        const result = await updateStorefrontDraft(draftUpdate);
        return result;
      }, 3000),
    [],
  );

  // This is called every time there's a character change.
  const handleTextEdit = async (
    e: FormEvent<HTMLHeadingElement>,
    key: string,
    savingType: CreatorSavingType,
  ) => {
    let newText = (e.target as HTMLDivElement).textContent || "";

    if (key in characterMaxLengthsPerField && newText.length > characterMaxLengthsPerField[key]) {
      setIsSavingChanges(false);
      setSavingStatusText(CHANGES_LIMIT_TEXT);
      setFieldsOnError((prevState) => prevState.concat(key));
      e.preventDefault();
      return;
    } else {
      // remove the field from being in error
      const updatedFields = fieldsOnError.filter((field) => field !== key);
      setFieldsOnError(updatedFields);
    }

    let keyToSave = key;
    // Storefronts and Products both have "description" as a property. So here we're using storefrontDescription to identify the storefront description. But when we want to save it in the storefront object in the backend, we send the actual property name, "description".
    if (key == "storefrontDescription") {
      keyToSave = "description";
    }
    saveUpdate(savingType, true, keyToSave, newText, setIsSavingChanges, setSavingStatusText);
  };

  // This updates a text field with a given text
  const handleTextFieldUpdate = async (
    text: string,
    key: string,
    savingType: CreatorSavingType,
  ) => {
    let newText = text || "";

    // Trim length code:
    // if (key in characterMaxLengthsPerField && newText.length > characterMaxLengthsPerField[key]) {
    //   const maxLength = characterMaxLengthsPerField[key];
    //   //newText = newText.slice(0, maxLength);
    //   setFieldsTexts((prevState) => ({ ...prevState, key: newText }));
    //   return;
    // }


    // Create new object so the object ref changes and updates in the context values.
    const newObj = { ...fieldsTexts };
    newObj[key as keyof CreatorFieldsTexts] = newText;
    setFieldsTexts(newObj);
    let keyToSave = key;
    // Storefronts and Products both have "description" as a property. So here we're using storefrontDescription to identify the storefront description. But when we want to save it in the storefront object in the backend, we send the actual property name, "description".
    if (key == "storefrontDescription") {
      keyToSave = "description";
    }
    saveUpdate(savingType, false, key, newText, setIsSavingChanges, setSavingStatusText);
  };

  // This is called every time the user "finishes" a session of editing (clicks out).
  const handleTextChange = async (
    e: FormEvent<HTMLHeadingElement>,
    key: string,
    savingType: CreatorSavingType,
  ) => {
    let newText = (e.target as HTMLDivElement).textContent || "";
    if (key in characterMaxLengthsPerField && newText.length > characterMaxLengthsPerField[key]) {
      e.preventDefault();
      const maxLength = characterMaxLengthsPerField[key];
      newText = newText.slice(0, maxLength);
      setFieldsTexts((prevState) => ({ ...prevState, key: newText }));
      return;
    }


    // Create new object so the object ref changes and updates in the context values.
    const newObj = { ...fieldsTexts };
    newObj[key as keyof CreatorFieldsTexts] = newText;
    setFieldsTexts(newObj);
    let keyToSave = key;
    // Storefronts and Products both have "description" as a property. So here we're using storefrontDescription to identify the storefront description. But when we want to save it in the storefront object in the backend, we send the actual property name, "description".
    if (key == "storefrontDescription") {
      keyToSave = "description";
    }
    saveUpdate(savingType, false, key, newText, setIsSavingChanges, setSavingStatusText);
  };

  const saveUpdate = (
    savingType: CreatorSavingType,
    shouldThrottle: boolean,
    key: string,
    newText: string,
    setIsSavingChanges: (isSaving: boolean) => void,
    setSavingStatusText: (text: string) => void,
  ) => {
    setIsSavingChanges(true);
    setSavingStatusText(CHANGES_SAVING_TEXT);
    if (savingType == CreatorSavingType.STOREFRONT) {
      saveStorefrontDraftUpdate(
        storefrontData as StorefrontData,
        shouldThrottle,
        key,
        newText,
      ).then((res) => {
        setIsSavingChanges(false);
        if (res.status == ApiStatus.SUCCESS) {
          setSavingStatusText(CHANGES_SAVED_TEXT);
        } else {
          setSavingStatusText(CHANGES_ERROR_TEXT);
        }
      });
    } else {
      saveProductDraftUpdate(productData, shouldThrottle, key, newText).then((res) => {
        if (res.status == ApiStatus.SUCCESS) {
          setSavingStatusText(CHANGES_SAVED_TEXT);
        } else {
          setSavingStatusText(CHANGES_ERROR_TEXT);
        }
      });
    }
  };

  const contextValue = useMemo(
    () => ({
      saveProductDraftUpdate,
      saveStorefrontDraftUpdate,
      createNewProductDraft,
      CHANGES_SAVING_TEXT,
      CHANGES_SAVED_TEXT,
      CHANGES_ERROR_TEXT,
      CHANGES_LIMIT_TEXT,
      fieldsTexts,
      fieldsOnError,
      handleTextEdit,
      handleTextChange,
      handleTextFieldUpdate,
      savingStatusText,
      isSavingChanges,
      setSavingStatusText,
      setIsSavingChanges,
      isAISuggestionsShown,
      handleSetIsAISuggestionsShown,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldsTexts, fieldsOnError, savingStatusText, fieldsTexts["creatorName"]],
  );

  return (
    <CreatorEditingContext.Provider value={contextValue}>{children}</CreatorEditingContext.Provider>
  );
};

export const useClickOutside = (handleClose: Function) => {
  const ref = useRef(null);

  useEffect(() => {
    let handler = (e: Event) => {
      // @ts-ignore
      if (!ref?.current?.contains(e.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  });

  return { ref };
};
